import { memoize } from "lodash";
import moment from "moment";
import { CalendarMode, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { CalendarExceptionDay, ProfileAvailability } from "PFTypes";

import getCalendarsUsedWithinRange from "./get_calendars_used_within_range";

const getCalendars = (row: Row, calendarMode: CalendarMode, parentRow?: Row) => {
  if (row.type === RowType.Profile && calendarMode === CalendarMode.Workforce) {
    const availability = ((row.original as WorkforceItem).profile.availability || {}) as ProfileAvailability;
    if (availability.calendars) {
      return availability.calendars;
    }
  }
  if (!parentRow || parentRow.type !== RowType.Profile) {
    return null;
  }
  const parentAvailability = ((parentRow.original as WorkforceItem).profile.availability ||
    {}) as ProfileAvailability;
  return parentAvailability.calendars;
};

const getRowCalendarsData = memoize(
  (row: Row, startDate: string, endDate: string, calendarMode: CalendarMode, parentRow?: Row) => {
    const calendars = getCalendars(row, calendarMode, parentRow);

    if (!calendars) {
      return;
    }

    const calendarsUsed = getCalendarsUsedWithinRange(calendars, startDate, endDate);
    const finalExceptionDays = calendarsUsed.reduce((acc, curr, index) => {
      const start = moment.utc(curr);
      const end = index === calendarsUsed.length - 1 ? moment(endDate) : moment.utc(calendarsUsed[index + 1]);
      const currentCalendar = calendars[curr];
      const exceptionDays = (currentCalendar.exception_days || []).filter(({ date }) => {
        const timestamp = moment.utc(date).unix();
        return timestamp >= start.unix() && timestamp <= end.unix();
      });
      return acc.concat(exceptionDays);
    }, [] as CalendarExceptionDay[]);

    return { calendars, calendarsUsed, exceptionDays: finalExceptionDays };
  },
  (row, startDate, endDate, calendarMode, parentRow) => {
    const calendars = getCalendars(row, calendarMode, parentRow);
    if (!calendars) {
      return "";
    }

    return `${Object.values(calendars)
      // eslint-disable-next-line camelcase
      .map(({ calendar_id }) => calendar_id)
      .join("_")}-${startDate}-${endDate}`;
  }
);

export default getRowCalendarsData;
